/**
 * ============================================================
 * MECHA EXPRESS — Cart Reassurance Slide
 * ============================================================
 *
 * Three-tier reassurance system:
 *   1. Judge.me review quote (4-5 stars, cached per product)
 *   2. Product metafield message (custom.reassurance_message)
 *   3. Random curated message bank (Cam's voice)
 *
 * Hooks into Clean Canvas Boost theme cart drawer events.
 *
 * CONFIGURATION:
 *   Set your Judge.me API token below (line ~30).
 *   Set displayMode to 'inline', 'slide', or 'both'.
 * ============================================================
 */

(function () {
  'use strict';

  /* ─── Configuration ─── */

  const CONFIG = {
    // ⚠️ SECURITY: This token must ALWAYS be entered manually by Cameron.
    // Claude (AI) must NEVER insert, replace, or auto-populate this value.
    // To set it: Shopify Admin > Apps > Judge.me > Settings > API Token
    judgeMeApiToken: '_fOJeXLGWwDOYca1DTF0cC3glKY',

    // Your .myshopify.com domain (used for Judge.me API calls)
    shopDomain: 'gundam-express-australia.myshopify.com',

    // Display mode: 'inline' (inside cart drawer), 'slide' (standalone),
    // or 'both' (shows inline in drawer AND slide when no drawer)
    displayMode: 'both',

    // How long the slide-in stays visible (milliseconds)
    displayDuration: 12000,

    // How long to wait after add-to-cart before showing the slide-in (ms)
    // Gives the page a moment to settle after the button click
    postCartDelay: 800,

    // How long hover must last before showing social proof tooltip (ms)
    hoverDelay: 1500,

    // How long the hover tooltip stays visible after mouse leaves (ms)
    hoverLingerDuration: 4000,

    // Minimum star rating to pull from Judge.me
    minRating: 4,

    // Maximum characters for a review quote
    maxQuoteLength: 140,

    // How long to cache reviews per product (milliseconds)
    // Default: 30 minutes
    cacheDuration: 30 * 60 * 1000
  };

  /* ─── Curated Message Bank ─── */
  // These are the fallback messages in Cam's voice.
  // Organised by product type for smarter matching,
  // plus a 'general' pool for everything else.

  const MESSAGE_BANK = {
    general: [
      {
        heading: 'Let\'s go!',
        message: "This kit is an absolute beast. You're gonna have a sick time building it.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'W choice!',
        message: "Honestly? This one hits different. The detail is insane.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Locked in!',
        message: "That's a fire pick. Throw on a playlist and enjoy the build.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'You know what\'s up!',
        message: "This kit flies off the shelf for a reason. Gonna look so clean on your shelf.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Nailed it!',
        message: "Can't go wrong with this one. The finished build goes crazy.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'No cap, great pick!',
        message: "Our customers go crazy for this one. Trust me, it's worth every minute.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Sheesh!',
        message: "This is the kind of kit that keeps our builders coming back. Pure vibes.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Big W!',
        message: "The detail on this kit is next level. You're not gonna be disappointed.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Hard to beat this one!',
        message: "Just got these off the truck and they're already flying out. You picked a banger.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Solid choice!',
        message: "This kit is lowkey one of the best value builds we stock. Enjoy it!",
        attribution: '— Cam, Mecha Express'
      }
    ],

    // Master Grade specific
    mg: [
      {
        heading: 'MG mode activated!',
        message: "Master Grades are where it's at. Our builders reckon this one's a beast.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Elite MG pick!',
        message: "The engineering on this MG is insane. Clear the desk, put on some lo-fi, and enjoy.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'MG gang!',
        message: "Once you go Master Grade you don't go back. This one's a top-tier build.",
        attribution: '— Cam, Mecha Express'
      }
    ],

    // Perfect Grade specific
    pg: [
      {
        heading: 'PG energy!',
        message: "You absolute legend. Clear the whole weekend for this one. It's gonna be unreal.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Going full PG!',
        message: "This is endgame territory. The finished build is gonna be a showstopper.",
        attribution: '— Cam, Mecha Express'
      }
    ],

    // High Grade specific
    hg: [
      {
        heading: 'HG vibes!',
        message: "Quick build, massive satisfaction. This HG punches way above its weight.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Clean HG!',
        message: "HGs are lowkey the most fun. No stress, just a good time. This one's a ripper.",
        attribution: '— Cam, Mecha Express'
      }
    ],

    // Real Grade specific
    rg: [
      {
        heading: 'RG supremacy!',
        message: "All the detail of an MG in a tiny package. Real Grades just hit different.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'RG is the way!',
        message: "The amount of detail they pack into an RG is honestly ridiculous. You'll see.",
        attribution: '— Cam, Mecha Express'
      }
    ],

    // Tools and supplies
    tools: [
      {
        heading: 'Level up!',
        message: "Good tools = cleaner builds. Your kits are gonna look so much better with these.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Big brain move!',
        message: "Investing in your setup is the play. Every build from here gets an upgrade.",
        attribution: '— Cam, Mecha Express'
      }
    ],

    // P-Bandai / limited items
    limited: [
      {
        heading: 'You secured the bag!',
        message: "These don't hang around. Glad you locked yours in before they're gone for good.",
        attribution: '— Cam, Mecha Express'
      },
      {
        heading: 'Rare pull!',
        message: "Limited run kit, and you got one. That's a W right there.",
        attribution: '— Cam, Mecha Express'
      }
    ]
  };

  /* ─── Heading Variants for Review Quotes ─── */
  const REVIEW_HEADINGS = [
    'Builders rate this one!',
    'From a fellow builder:',
    'The community has spoken:',
    'Real talk from a customer:',
    'Don\'t just take our word for it:'
  ];

  /* ─── State ─── */

  // Cache: { [productHandle]: { reviews: [...], timestamp: Date.now() } }
  const reviewCache = {};

  // Deduplicate in-flight review requests so pre-fetch and slide-in
  // share the same Promise instead of firing duplicate API calls.
  const inFlightReviews = {};

  // Track last product added (to avoid repeat messages on same product)
  let lastProductId = null;

  // Timer for auto-dismiss
  let dismissTimer = null;

  // Hover tooltip state
  let hoverTimer = null;
  let hoverLingerTimer = null;
  let hoverShownForProduct = null;

  // Once a customer clicks Add to Bag, suppress the hover tooltip entirely.
  // The slide-in handles post-purchase reassurance — showing both is distracting.
  let cartAdded = false;

  // Prevents the form-submit fallback (Method 3) from firing when
  // jQuery AJAX (Method 1) or Fetch (Method 2) has already handled it.
  let ajaxInterceptorHandled = false;

  /* ─── DOM References ─── */

  function getElements() {
    return {
      inline: document.getElementById('mecha-reassurance-inline'),
      slide: document.getElementById('mecha-reassurance-slide'),
      hover: document.getElementById('mecha-reassurance-hover')
    };
  }

  /* ─── Utility Functions ─── */

  function randomItem(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }

  function truncateQuote(text, maxLen) {
    if (text.length <= maxLen) return text;
    // Cut at last full word within limit, add ellipsis
    const truncated = text.substring(0, maxLen).replace(/\s+\S*$/, '');
    return truncated + '...';
  }

  /**
   * Detect the product "grade" from its title or type for
   * smarter message matching.
   */
  function detectGrade(title, type) {
    const combined = (title + ' ' + type).toLowerCase();

    if (/\bpg\b|perfect\s*grade/i.test(combined)) return 'pg';
    if (/\bmg\b|master\s*grade/i.test(combined)) return 'mg';
    if (/\brg\b|real\s*grade/i.test(combined)) return 'rg';
    if (/\bhg\b|high\s*grade|hguc|hgce|hgbf/i.test(combined)) return 'hg';
    if (/tool|nipper|marker|cement|paint|thinner|putty|file|sandpaper/i.test(combined)) return 'tools';
    if (/p-bandai|pbandai|premium\s*bandai|limited|exclusive/i.test(combined)) return 'limited';

    return 'general';
  }

  /* ─── Date Helpers ─── */

  /**
   * Format a Date as 'YYYY-MM-DD' using LOCAL time (not UTC).
   * Using toISOString() would shift dates by a day in AEST.
   */
  function dateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /* ─── QLD Public Holiday Calendar ─── */

  /**
   * Compute Easter Sunday for a given year using the
   * Anonymous Gregorian algorithm (Computus).
   * Returns a Date object for Easter Sunday.
   */
  function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }

  /**
   * Get the Nth occurrence of a weekday in a given month/year.
   * weekday: 0=Sun, 1=Mon, ... 6=Sat
   * n: 1=first, 2=second, etc.
   */
  function nthWeekdayOfMonth(year, month, weekday, n) {
    const first = new Date(year, month, 1);
    let dayOfWeek = first.getDay();
    let diff = weekday - dayOfWeek;
    if (diff < 0) diff += 7;
    const day = 1 + diff + (n - 1) * 7;
    return new Date(year, month, day);
  }

  /**
   * If a fixed holiday falls on a weekend, find the substitute weekday.
   * Sat → Mon, Sun → Mon (or Tue if Mon is also taken).
   */
  function substituteDay(date, alreadyTaken) {
    const day = date.getDay();
    if (day === 6) {
      // Saturday → Monday
      const mon = new Date(date);
      mon.setDate(mon.getDate() + 2);
      if (alreadyTaken && alreadyTaken.getTime() === mon.getTime()) {
        const tue = new Date(mon);
        tue.setDate(tue.getDate() + 1);
        return tue;
      }
      return mon;
    }
    if (day === 0) {
      // Sunday → Monday
      const mon = new Date(date);
      mon.setDate(mon.getDate() + 1);
      if (alreadyTaken && alreadyTaken.getTime() === mon.getTime()) {
        const tue = new Date(mon);
        tue.setDate(tue.getDate() + 1);
        return tue;
      }
      return mon;
    }
    return date;
  }

  /**
   * Build a Set of holiday date strings (YYYY-MM-DD) for
   * Queensland (Brisbane) for the given year.
   *
   * Includes all statewide public holidays plus Ekka (Brisbane only).
   * Auto-calculates Easter and moveable holidays.
   */
  function getQldHolidays(year) {
    const holidays = new Map(); // dateStr → holiday name

    function addHoliday(date, name) {
      holidays.set(dateKey(date), name);
    }

    // ── Fixed-date holidays ──

    // New Year's Day (1 Jan, substitute if weekend)
    const nyd = new Date(year, 0, 1);
    addHoliday(substituteDay(nyd), "New Year's Day");

    // Australia Day (26 Jan, substitute if weekend)
    const ad = new Date(year, 0, 26);
    addHoliday(substituteDay(ad), 'Australia Day');

    // ANZAC Day (25 Apr) — QLD does NOT substitute if on weekend
    const anzac = new Date(year, 3, 25);
    if (anzac.getDay() !== 0 && anzac.getDay() !== 6) {
      addHoliday(anzac, 'ANZAC Day');
    }
    // Note: If ANZAC falls on weekend in QLD, no public holiday observed

    // Christmas Day (25 Dec) and Boxing Day (26 Dec)
    const xmas = new Date(year, 11, 25);
    const boxing = new Date(year, 11, 26);

    if (xmas.getDay() === 0) {
      // Xmas Sun → Mon, Boxing Mon → Tue
      addHoliday(new Date(year, 11, 27), 'Christmas Day (substitute)');
      addHoliday(new Date(year, 11, 28), 'Boxing Day (substitute)');
      addHoliday(xmas, 'Christmas Day');
      addHoliday(boxing, 'Boxing Day');
    } else if (xmas.getDay() === 6) {
      // Xmas Sat → Mon, Boxing Sun → Tue
      addHoliday(new Date(year, 11, 27), 'Christmas Day (substitute)');
      addHoliday(new Date(year, 11, 28), 'Boxing Day (substitute)');
      addHoliday(xmas, 'Christmas Day');
      addHoliday(boxing, 'Boxing Day');
    } else {
      addHoliday(xmas, 'Christmas Day');
      if (boxing.getDay() === 6 || boxing.getDay() === 0) {
        addHoliday(substituteDay(boxing, xmas), 'Boxing Day (substitute)');
      } else {
        addHoliday(boxing, 'Boxing Day');
      }
    }

    // Christmas Eve (half-day from 6pm, but we treat as no-dispatch
    // since our cutoff is 2pm — already past business hours)
    // No action needed as 6pm is after our 2pm cutoff

    // ── Easter (calculated) ──
    const easter = easterSunday(year);

    const goodFriday = new Date(easter);
    goodFriday.setDate(easter.getDate() - 2);
    addHoliday(goodFriday, 'Good Friday');

    const easterSat = new Date(easter);
    easterSat.setDate(easter.getDate() - 1);
    addHoliday(easterSat, 'Easter Saturday');

    addHoliday(easter, 'Easter Sunday');

    const easterMon = new Date(easter);
    easterMon.setDate(easter.getDate() + 1);
    addHoliday(easterMon, 'Easter Monday');

    // ── King's Birthday (QLD: last Monday in October) ──
    // QLD moved from 2nd Mon in June to last Mon in Oct from 2023
    const oct31 = new Date(year, 9, 31);
    let lastMonOct = new Date(oct31);
    while (lastMonOct.getDay() !== 1) {
      lastMonOct.setDate(lastMonOct.getDate() - 1);
    }
    addHoliday(lastMonOct, "King's Birthday");

    // ── Royal Queensland Show (Ekka) — Brisbane only ──
    // Wednesday in the week starting with the 2nd-last Monday of Aug.
    // Simplified: typically the Wednesday 10–16 August range.
    // Rule: Count Mondays in August. Ekka Wed is in the week of
    // the second-to-last Monday.
    const augMondays = [];
    for (let d = 1; d <= 31; d++) {
      const dt = new Date(year, 7, d);
      if (dt.getMonth() !== 7) break; // past August
      if (dt.getDay() === 1) augMondays.push(dt);
    }
    if (augMondays.length >= 2) {
      const secondLastMon = augMondays[augMondays.length - 2];
      const ekkaWed = new Date(secondLastMon);
      ekkaWed.setDate(secondLastMon.getDate() + 2); // Mon + 2 = Wed
      addHoliday(ekkaWed, 'Royal Queensland Show (Ekka)');
    }

    return holidays;
  }

  // Build holiday caches for current year and next year
  // so we can look ahead across year boundaries (e.g. late Dec)
  let holidayCache = null;

  function getHolidayMap() {
    if (holidayCache) return holidayCache;

    const now = new Date();
    const year = now.getFullYear();
    const thisYear = getQldHolidays(year);
    const nextYear = getQldHolidays(year + 1);

    // Merge both years into one map
    holidayCache = new Map([...thisYear, ...nextYear]);
    return holidayCache;
  }

  /**
   * Check if a given date (YYYY-MM-DD or Date object) is a QLD holiday.
   * Returns the holiday name if it is, or null if not.
   */
  function isHoliday(date) {
    const holidays = getHolidayMap();
    const key = date instanceof Date
      ? dateKey(date)
      : date;
    return holidays.get(key) || null;
  }

  /**
   * Check if a date is a non-business day (weekend OR public holiday).
   */
  function isNonBusinessDay(date) {
    const day = date.getDay();
    if (day === 0 || day === 6) return true;
    return !!isHoliday(date);
  }

  /**
   * Find the next business day from a given date.
   * Skips weekends and QLD public holidays.
   */
  function nextBusinessDay(fromDate) {
    const next = new Date(fromDate);
    next.setDate(next.getDate() + 1);
    while (isNonBusinessDay(next)) {
      next.setDate(next.getDate() + 1);
    }
    return next;
  }

  /* ─── Shipping Reassurance (Time-Aware + Holiday-Aware) ─── */

  /**
   * Generate a shipping reassurance message based on the
   * customer's current time in AEST (UTC+10), accounting
   * for weekends AND QLD public holidays.
   *
   * Logic:
   *   - Before 2pm on a business day → "ships today"
   *   - After 2pm, or on a non-business day → find next business day
   *   - If holidays cause multi-day gap, explain why with the date
   */
  function getShippingMessage() {
    // Get current time in AEST (Australia/Brisbane, no daylight saving)
    const now = new Date();
    const aestOptions = { timeZone: 'Australia/Brisbane', hour12: false };

    const aestHour = parseInt(
      now.toLocaleString('en-AU', { ...aestOptions, hour: 'numeric' }), 10
    );

    // Build the "today" date in AEST
    const aestDateStr = now.toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' });
    const [y, m, d] = aestDateStr.split('-').map(Number);
    const todayAest = new Date(y, m - 1, d);

    const isBefore2pm = aestHour < 14;
    const todayIsBusinessDay = !isNonBusinessDay(todayAest);

    const icon = '\u{1F69A}';

    // Can we dispatch today?
    if (todayIsBusinessDay && isBefore2pm) {
      return `${icon} Order now and we'll have this shipped today by 2pm AEST`;
    }

    // Find next business day
    const dispatchDate = isBefore2pm && !todayIsBusinessDay
      ? (function() {
          // It's before 2pm but today is a holiday/weekend.
          // Next business day from today.
          return nextBusinessDay(todayAest);
        })()
      : nextBusinessDay(todayAest);

    // Calculate days gap
    const diffMs = dispatchDate.getTime() - todayAest.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    // Format the dispatch date nicely: "Tuesday 7 April"
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const dispatchDayName = dayNames[dispatchDate.getDay()];
    const dispatchDateFormatted = `${dispatchDayName} ${dispatchDate.getDate()} ${monthNames[dispatchDate.getMonth()]}`;

    // If it's just tomorrow (normal after-2pm weekday), keep it casual
    if (diffDays === 1) {
      return `${icon} We'll get this dispatched tomorrow by 2pm AEST`;
    }

    // If next business day is within a normal weekend gap (2-3 days)
    // and no holidays are involved, keep it simple
    const holidaysInGap = [];
    const check = new Date(todayAest);
    for (let i = 0; i < diffDays; i++) {
      check.setDate(check.getDate() + (i === 0 ? 1 : 1));
      if (i > 0) continue; // skip first iteration's double-add
      // Actually let's just check properly
    }
    // Re-check holidays between today and dispatch date
    const gapCheck = new Date(todayAest);
    gapCheck.setDate(gapCheck.getDate() + 1);
    while (gapCheck < dispatchDate) {
      const hol = isHoliday(gapCheck);
      if (hol) holidaysInGap.push(hol);
      gapCheck.setDate(gapCheck.getDate() + 1);
    }

    // Also check if today itself is a holiday (for the message context)
    const todayHoliday = isHoliday(todayAest);

    // If there are public holidays in the gap, mention them
    if (holidaysInGap.length > 0 || todayHoliday) {
      // Deduplicate holiday names (e.g. "Easter" appears multiple times)
      const uniqueHolidays = [...new Set([
        ...(todayHoliday ? [todayHoliday] : []),
        ...holidaysInGap
      ])];

      // Group related holidays for cleaner messaging
      const hasEaster = uniqueHolidays.some(h => h.toLowerCase().includes('easter') || h.toLowerCase().includes('good friday'));
      const hasChristmas = uniqueHolidays.some(h => h.toLowerCase().includes('christmas') || h.toLowerCase().includes('boxing'));

      let holidayLabel;
      if (hasEaster) {
        holidayLabel = 'the Easter public holidays';
      } else if (hasChristmas) {
        holidayLabel = 'the Christmas public holidays';
      } else if (uniqueHolidays.length === 1) {
        holidayLabel = uniqueHolidays[0];
      } else {
        holidayLabel = 'public holidays';
      }

      return `${icon} Due to ${holidayLabel}, your order will be dispatched ${dispatchDateFormatted} by 2pm AEST`;
    }

    // Normal weekend gap, no holidays
    return `${icon} We'll have this dispatched ${dispatchDayName} by 2pm AEST`;
  }

  /* ─── Judge.me API Integration ─── */

  /**
   * Internal: perform the actual Judge.me API call and parse results.
   * Called by fetchReviews() which handles caching and deduplication.
   */
  async function _doFetchReviews(productHandle) {
    try {
      // Use the Judge.me widget endpoint (CORS-friendly, works from browsers).
      // The /api/v1/reviews endpoint blocks browser requests due to CORS.
      const url = new URL('https://judge.me/api/v1/widgets/product_review');
      url.searchParams.set('shop_domain', CONFIG.shopDomain);
      url.searchParams.set('api_token', CONFIG.judgeMeApiToken);
      url.searchParams.set('handle', productHandle);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        console.warn('[Mecha Reassurance] Judge.me widget API returned', response.status);
        reviewCache[productHandle] = { reviews: [], timestamp: Date.now() };
        return [];
      }

      const data = await response.json();

      // The widget returns HTML in data.widget — parse it to extract reviews
      const parser = new DOMParser();
      const doc = parser.parseFromString(data.widget || '', 'text/html');
      const reviewEls = doc.querySelectorAll('.jdgm-rev');

      const reviews = [];
      reviewEls.forEach(function (el) {
        const ratingAttr = el.querySelector('.jdgm-rev__rating');
        const rating = ratingAttr ? parseInt(ratingAttr.getAttribute('data-score'), 10) : 0;
        const bodyEl = el.querySelector('.jdgm-rev__body');
        const body = bodyEl ? bodyEl.textContent.trim() : '';
        const authorEl = el.querySelector('.jdgm-rev__author');
        const author = authorEl ? authorEl.textContent.trim() : '';

        if (rating >= CONFIG.minRating && body.length > 10) {
          reviews.push({
            body: truncateQuote(body, CONFIG.maxQuoteLength),
            reviewer: author ? author.split(' ')[0] : 'A verified buyer',
            rating: rating
          });
        }
      });

      // Cache the result
      reviewCache[productHandle] = { reviews, timestamp: Date.now() };
      console.log('[Mecha Reassurance] Fetched', reviews.length, 'reviews for', productHandle);
      return reviews;

    } catch (err) {
      console.warn('[Mecha Reassurance] Could not fetch Judge.me reviews:', err.message);
      reviewCache[productHandle] = { reviews: [], timestamp: Date.now() };
      return [];
    }
  }

  /**
   * Fetch reviews with caching and in-flight request deduplication.
   * If a fetch for the same handle is already in progress (e.g. the
   * pre-fetch from page load), callers join that same Promise instead
   * of firing a duplicate API call. This prevents the race condition
   * where add-to-cart fires before the pre-fetch has completed.
   */
  async function fetchReviews(productHandle) {
    if (!productHandle) return [];

    // Check cache first (keyed by handle)
    const cached = reviewCache[productHandle];
    if (cached && (Date.now() - cached.timestamp) < CONFIG.cacheDuration) {
      return cached.reviews;
    }

    // If a request for this handle is already in-flight, join it
    if (inFlightReviews[productHandle]) {
      console.log('[Mecha Reassurance] Joining in-flight review fetch for', productHandle);
      return inFlightReviews[productHandle];
    }

    // Start new request and track it
    const promise = _doFetchReviews(productHandle);
    inFlightReviews[productHandle] = promise;

    try {
      return await promise;
    } finally {
      delete inFlightReviews[productHandle];
    }
  }

  /* ─── Message Selection (Three-Tier Logic) ─── */

  /**
   * Determine the best reassurance message to show.
   *
   * Priority:
   *   1. Judge.me review (if available for this product)
   *   2. Product metafield reassurance_message
   *   3. Curated message bank (grade-matched, then general)
   */
  async function selectMessage(productHandle, productTitle, productType, metaReassurance) {
    // ── Tier 1: Judge.me review ──
    if (CONFIG.judgeMeApiToken && CONFIG.judgeMeApiToken !== 'YOUR_JUDGEME_API_TOKEN_HERE') {
      const reviews = await fetchReviews(productHandle);
      if (reviews.length > 0) {
        const review = randomItem(reviews);
        return {
          heading: randomItem(REVIEW_HEADINGS),
          message: `"${review.body}"`,
          attribution: `— ${review.reviewer}, verified buyer`
        };
      }
    }

    // ── Tier 2: Product metafield ──
    if (metaReassurance && metaReassurance.trim().length > 0) {
      return {
        heading: 'Cam says:',
        message: metaReassurance.trim(),
        attribution: '— Cam, Mecha Express'
      };
    }

    // ── Tier 3: Curated message bank ──
    const grade = detectGrade(productTitle || '', productType || '');
    const gradeMessages = MESSAGE_BANK[grade] || [];
    const pool = gradeMessages.length > 0
      ? [...gradeMessages, ...MESSAGE_BANK.general]
      : MESSAGE_BANK.general;

    return randomItem(pool);
  }

  /* ─── Display Logic ─── */

  function populateElement(el, data) {
    if (!el) return;

    const heading = el.querySelector('.mecha-reassurance__heading');
    const message = el.querySelector('.mecha-reassurance__message');
    const attribution = el.querySelector('.mecha-reassurance__attribution');
    const shipping = el.querySelector('.mecha-reassurance__shipping');

    if (heading) heading.textContent = data.heading;
    if (message) message.textContent = data.message;
    if (attribution) attribution.textContent = data.attribution;

    // Populate shipping reassurance line
    if (shipping) {
      shipping.textContent = getShippingMessage();
      shipping.hidden = false;
    }
  }

  function showReassurance(el) {
    if (!el) return;
    el.hidden = false;

    // Force reflow so the CSS transition kicks in
    void el.offsetHeight;
    el.classList.add('is-visible');
  }

  function hideReassurance(el) {
    if (!el) return;
    el.classList.remove('is-visible');

    // Wait for CSS transition to complete, then hide
    el.addEventListener('transitionend', function handler() {
      el.hidden = true;
      el.removeEventListener('transitionend', handler);
    }, { once: true });

    // Fallback if transitionend doesn't fire
    setTimeout(() => { el.hidden = true; }, 500);
  }

  function hideAll() {
    const els = getElements();
    hideReassurance(els.inline);
    hideReassurance(els.slide);
    clearTimeout(dismissTimer);
  }

  /**
   * Main trigger: show the reassurance message.
   *
   * @param {boolean} cartDrawerOpen - Whether the cart drawer is currently open/opening
   */
  let lastTriggerTime = 0;

  async function triggerReassurance(productData, cartDrawerOpen) {
    // Debounce: ignore duplicate triggers within 2 seconds
    // (multiple interceptors can catch the same add-to-cart event)
    const now = Date.now();
    if (now - lastTriggerTime < 2000) {
      console.log('[Mecha Reassurance] Duplicate trigger ignored (debounce)');
      return;
    }
    lastTriggerTime = now;

    const els = getElements();

    // Determine which element(s) to show
    let targetEl = null;
    if (CONFIG.displayMode === 'inline' && cartDrawerOpen) {
      targetEl = els.inline;
    } else if (CONFIG.displayMode === 'slide') {
      targetEl = els.slide;
    } else if (CONFIG.displayMode === 'both') {
      // Show inline if drawer is open, slide if not
      targetEl = cartDrawerOpen ? els.inline : els.slide;
    }

    if (!targetEl) return;

    // Select the message (pass handle for Judge.me widget API)
    const messageData = await selectMessage(
      productData.handle || '',
      productData.title,
      productData.type,
      productData.metaReassurance
    );

    // Populate and show
    populateElement(targetEl, messageData);
    showReassurance(targetEl);

    // Auto-dismiss after configured duration
    clearTimeout(dismissTimer);
    dismissTimer = setTimeout(() => {
      hideReassurance(targetEl);
    }, CONFIG.displayDuration);

    // Track last product to avoid immediate repeats
    lastProductId = productData.id;
  }

  /* ─── Event Listeners ─── */

  function init() {
    // Read initial product data from the Liquid-injected JSON
    const dataEl = document.getElementById('mecha-reassurance-data');
    let baseProductData = { id: null, handle: '', title: '', type: '', metaReassurance: '' };

    if (dataEl) {
      try {
        const parsed = JSON.parse(dataEl.textContent);
        baseProductData = parsed.currentProduct || baseProductData;
        // Override shop domain if provided by Liquid
        if (parsed.shopDomain) {
          CONFIG.shopDomain = parsed.shopDomain;
        }
      } catch (e) {
        console.warn('[Mecha Reassurance] Could not parse product data:', e);
      }
    }

    // Close button handlers (spans with role="button" need keyboard support)
    document.querySelectorAll('.mecha-reassurance__close').forEach(btn => {
      btn.addEventListener('click', hideAll);
      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hideAll(); }
      });
    });

    // ── Hover Social Proof (Pre-Purchase) ──
    // When a customer hovers over the Add to Bag button for 1.5s+,
    // show a review quote to provide social proof and reduce hesitation.
    // Only shows if there's a review available for this product.

    function setupHoverTrigger() {
      // Selector covers: standard Shopify button, common theme variants,
      // and Pre-Order Globo's injected button (.gPreorderBtn).
      const BTN_SELECTOR =
        'button[type="submit"][name="add"], ' +
        '.gPreorderBtn, ' +
        'button.product-add-to-cart, ' +
        'button.add-to-cart, ' +
        '#AddToCart, ' +
        '[data-action="add-to-cart"], ' +
        'form[action*="/cart/add"] button[type="submit"]';

      if (!baseProductData.id) return;

      const hoverEl = getElements().hover;
      if (!hoverEl) return;

      // Pre-fetch reviews immediately — don't wait for the button to appear.
      let cachedReviews = null;

      async function loadReviews() {
        if (CONFIG.judgeMeApiToken && CONFIG.judgeMeApiToken !== 'YOUR_JUDGEME_API_TOKEN_HERE') {
          cachedReviews = await fetchReviews(baseProductData.handle);
        }
      }
      loadReviews();

      // Returns the first visible button matching BTN_SELECTOR.
      // Skips hidden/zero-height matches (e.g. Boost theme loading state,
      // standard button hidden by Pre-Order Globo).
      function findVisibleBtn() {
        const candidates = document.querySelectorAll(BTN_SELECTOR);
        for (const btn of candidates) {
          const cs = window.getComputedStyle(btn);
          if (
            cs.visibility !== 'hidden' &&
            cs.display !== 'none' &&
            btn.getBoundingClientRect().height > 0
          ) {
            return btn;
          }
        }
        return null;
      }

      // Wire all hover events onto the resolved button.
      // Called once — either immediately if the button is already visible,
      // or by the MutationObserver when it becomes visible.
      function wireButton(addBtn) {

      function showHoverTooltip() {
        if (cartAdded) return;  // Post-purchase: slide-in handles it
        if (!cachedReviews || cachedReviews.length === 0) return;
        if (hoverShownForProduct === baseProductData.handle) return;

        const review = randomItem(cachedReviews);
        const quoteEl = hoverEl.querySelector('.mecha-reassurance-hover__quote');
        const reviewerEl = hoverEl.querySelector('.mecha-reassurance-hover__reviewer');

        if (quoteEl) quoteEl.textContent = '"' + review.body + '"';
        if (reviewerEl) reviewerEl.textContent = '— ' + review.reviewer + ', verified buyer';

        // Position the tooltip above the button
        // First make it visible but transparent so we can measure height
        hoverEl.hidden = false;
        hoverEl.style.visibility = 'hidden';
        void hoverEl.offsetHeight;

        const btnRect = addBtn.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const tooltipHeight = hoverEl.offsetHeight;

        hoverEl.style.position = 'absolute';
        hoverEl.style.left = btnRect.left + 'px';
        hoverEl.style.top = (btnRect.top + scrollTop - tooltipHeight - 12) + 'px';
        hoverEl.style.visibility = '';

        void hoverEl.offsetHeight;
        hoverEl.classList.add('is-visible');

        hoverShownForProduct = baseProductData.handle;
        console.log('[Mecha Reassurance] Hover tooltip shown for', baseProductData.title);
      }

      function hideHoverTooltip() {
        hoverEl.classList.remove('is-visible');
        setTimeout(() => { hoverEl.hidden = true; }, 300);
      }

      // Mouse enters button → start delay timer
      addBtn.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimer);
        clearTimeout(hoverLingerTimer);
        hoverTimer = setTimeout(showHoverTooltip, CONFIG.hoverDelay);
      });

      // Mouse leaves button → start linger timer (tooltip stays briefly)
      addBtn.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimer);
        // If tooltip is visible, let it linger then hide
        if (hoverEl.classList.contains('is-visible')) {
          hoverLingerTimer = setTimeout(hideHoverTooltip, CONFIG.hoverLingerDuration);
        }
      });

      // Mouse enters tooltip → cancel the linger timer (keep it visible)
      hoverEl.addEventListener('mouseenter', () => {
        clearTimeout(hoverLingerTimer);
      });

      // Mouse leaves tooltip → hide after linger duration
      hoverEl.addEventListener('mouseleave', () => {
        hoverLingerTimer = setTimeout(hideHoverTooltip, CONFIG.hoverLingerDuration);
      });

      // Click on Add to Bag → immediately kill hover (don't wait for AJAX response).
      // This fires synchronously on click, so the hover timer can't sneak in
      // between the click and the AJAX completion.
      addBtn.addEventListener('click', () => {
        cartAdded = true;
        clearTimeout(hoverTimer);
        clearTimeout(hoverLingerTimer);
        hideHoverTooltip();
      });

      // Close button on tooltip
      const closeBtn = hoverEl.querySelector('.mecha-reassurance__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          hideHoverTooltip();
        });
      }

      console.log('[Mecha Reassurance] Hover trigger active on Add to Bag button');
      } // end wireButton

      // Try immediately first
      const immediateBtn = findVisibleBtn();
      if (immediateBtn) {
        wireButton(immediateBtn);
        return;
      }

      // Button not visible yet (e.g. Boost loading state, Globo injecting async).
      // Watch the DOM for it to appear or become visible.
      console.log('[Mecha Reassurance] Add-to-cart button not visible yet — watching for it...');
      let observer = null;

      const giveUpTimer = setTimeout(() => {
        if (observer) { observer.disconnect(); observer = null; }
        console.log('[Mecha Reassurance] No add-to-cart button found after 5s, hover trigger inactive');
      }, 5000);

      observer = new MutationObserver(() => {
        const btn = findVisibleBtn();
        if (btn) {
          clearTimeout(giveUpTimer);
          observer.disconnect();
          observer = null;
          wireButton(btn);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    } // end setupHoverTrigger

    setupHoverTrigger();

    // ── Check for pending reassurance from a previous page ──
    // If the theme redirected to /cart after add-to-cart, the product
    // data was stored in sessionStorage. Show it now.

    try {
      const pending = sessionStorage.getItem('mecha_reassurance_pending');
      if (pending) {
        const { productData, timestamp } = JSON.parse(pending);
        // Only use it if it's less than 30 seconds old
        if (productData && (Date.now() - timestamp) < 30000) {
          sessionStorage.removeItem('mecha_reassurance_pending');
          // Show the slide version on the cart page (no cart drawer)
          setTimeout(() => {
            triggerReassurance(productData, false);
          }, 600);
        } else {
          sessionStorage.removeItem('mecha_reassurance_pending');
        }
      }
    } catch (e) {
      // sessionStorage not available, skip
    }

    // ── Boost Theme: Cart Drawer Events ──
    // The Boost theme by Clean Canvas dispatches these custom events.

    let cartDrawerIsOpen = false;

    document.addEventListener('on:cart-drawer:after-open', () => {
      cartDrawerIsOpen = true;
    });

    document.addEventListener('on:cart-drawer:after-close', () => {
      cartDrawerIsOpen = false;
      // Hide inline reassurance when drawer closes
      const els = getElements();
      hideReassurance(els.inline);
    });

    // ── Helper: Process a successful cart/add response ──
    // When the theme uses a cart drawer, we show the reassurance
    // immediately. When the theme redirects to the /cart page,
    // we store the product data in sessionStorage so it survives
    // the page navigation, then show it on the cart page.

    function handleCartAddResponse(cartData) {
      ajaxInterceptorHandled = true;

      const addedItem = cartData.items
        ? cartData.items[0]
        : cartData;

      if (addedItem && addedItem.product_id) {
        const productData = {
          id: addedItem.product_id,
          handle: addedItem.handle || baseProductData.handle || '',
          title: addedItem.product_title || addedItem.title || '',
          type: addedItem.product_type || '',
          metaReassurance: baseProductData.metaReassurance || ''
        };

        // Save to sessionStorage so it persists across page redirect
        try {
          sessionStorage.setItem('mecha_reassurance_pending', JSON.stringify({
            productData: productData,
            timestamp: Date.now()
          }));
        } catch (e) {
          // sessionStorage not available, proceed without it
        }

        // Suppress hover tooltip now that customer has committed to purchase.
        // The slide-in handles post-purchase reassurance from here.
        cartAdded = true;
        const hoverEl = getElements().hover;
        if (hoverEl) { hoverEl.classList.remove('is-visible'); hoverEl.hidden = true; }
        clearTimeout(hoverTimer);
        clearTimeout(hoverLingerTimer);

        setTimeout(() => {
          triggerReassurance(productData, cartDrawerIsOpen);
        }, CONFIG.postCartDelay);
      }
    }

    // ── Method 1: Intercept jQuery AJAX (used by Boost theme) ──
    // The Boost theme by Clean Canvas uses jQuery.ajax() to POST
    // to /cart/add.js. This is the primary trigger.

    if (window.jQuery) {
      jQuery(document).ajaxComplete(function (event, jqXHR, settings) {
        if (settings.url && settings.url.includes('/cart/add') &&
            settings.type && settings.type.toUpperCase() === 'POST' &&
            jqXHR.status >= 200 && jqXHR.status < 300) {
          try {
            const cartData = typeof jqXHR.responseJSON !== 'undefined'
              ? jqXHR.responseJSON
              : JSON.parse(jqXHR.responseText);
            handleCartAddResponse(cartData);
          } catch (err) {
            // If we can't parse the response, use base product data
            console.warn('[Mecha Reassurance] Could not parse jQuery AJAX response:', err);
            if (baseProductData.id) {
              setTimeout(() => {
                triggerReassurance(baseProductData, cartDrawerIsOpen);
              }, 400);
            }
          }
        }
      });
      console.log('[Mecha Reassurance] jQuery AJAX interceptor active');
    }

    // ── Method 2: Intercept native Fetch (fallback for other themes) ──
    // Some themes use fetch instead of jQuery. We keep this as a
    // secondary trigger in case the theme changes or other scripts
    // add to cart via fetch.

    const originalFetch = window.fetch;

    window.fetch = function (...args) {
      const [resource, options] = args;
      const url = typeof resource === 'string' ? resource : resource.url;

      if (url && url.includes('/cart/add') && options && options.method === 'POST') {
        return originalFetch.apply(this, args).then(response => {
          if (response.ok) {
            const clonedResponse = response.clone();
            clonedResponse.json().then(cartData => {
              handleCartAddResponse(cartData);
            }).catch(() => {
              if (baseProductData.id) {
                setTimeout(() => {
                  triggerReassurance(baseProductData, cartDrawerIsOpen);
                }, 400);
              }
            });
          }
          return response;
        });
      }

      return originalFetch.apply(this, args);
    };

    // ── Method 3: Listen for form submissions (final fallback) ──
    // Only fires if neither jQuery AJAX (Method 1) nor Fetch (Method 2)
    // handled the response. This prevents double-triggering on themes
    // that use AJAX to submit the form.

    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form && form.action && form.action.includes('/cart/add')) {
        if (baseProductData.id) {
          ajaxInterceptorHandled = false; // Reset; give AJAX interceptors time to claim it
          setTimeout(() => {
            if (!ajaxInterceptorHandled) {
              console.log('[Mecha Reassurance] Form submit fallback triggered (no AJAX interceptor fired)');
              triggerReassurance(baseProductData, false);
            } else {
              console.log('[Mecha Reassurance] Form submit fallback skipped (AJAX interceptor handled it)');
            }
          }, 800);
        }
      }
    });

    // ── Pre-fetch reviews for the current product page ──
    // This avoids a delay on first add-to-cart
    if (baseProductData.handle &&
        CONFIG.judgeMeApiToken &&
        CONFIG.judgeMeApiToken !== '_fOJeXLGWwDOYca1DTF0cC3glKY') {
      fetchReviews(baseProductData.handle);
    }

    console.log('[Mecha Reassurance] Initialised. Display mode:', CONFIG.displayMode);
  }

  // ── Boot Sequence ──
  // The Boost theme loads jQuery AFTER our script in the DOM,
  // so we need to wait until the full page (including all scripts)
  // has loaded before initialising. Using 'load' event ensures
  // jQuery and all other dependencies are available.

  function boot() {
    // If jQuery still isn't loaded, poll for it (up to 5 seconds)
    if (typeof window.jQuery === 'undefined') {
      console.log('[Mecha Reassurance] Waiting for jQuery...');
      let attempts = 0;
      const jqPoll = setInterval(() => {
        attempts++;
        if (typeof window.jQuery !== 'undefined') {
          clearInterval(jqPoll);
          console.log('[Mecha Reassurance] jQuery found after', attempts * 100, 'ms');
          init();
        } else if (attempts > 50) {
          clearInterval(jqPoll);
          console.warn('[Mecha Reassurance] jQuery not found after 5s, initialising without it');
          init();
        }
      }, 100);
    } else {
      init();
    }
  }

  // Track whether boot has run to prevent double-init
  let booted = false;
  function safeBoot() {
    if (booted) return;
    booted = true;
    boot();
  }

  if (document.readyState === 'complete') {
    // Page already loaded (e.g. script re-injected or very late load)
    safeBoot();
  } else {
    // Normal case: wait for full page load so jQuery is available
    window.addEventListener('load', safeBoot);
    // Safety net: if the load event was somehow missed (Shopify CDN timing),
    // poll readyState and boot once the page is complete
    var readyPoll = setInterval(function () {
      if (document.readyState === 'complete') {
        clearInterval(readyPoll);
        safeBoot();
      }
    }, 200);
  }

})();
